/**
 * "Continue with Google" — Google's own button (Google Identity Services), so
 * the branding rules are Google's. The "or" divider around it is the sign-in
 * form's (it also holds the Microsoft button).
 *
 * The GIS script is loaded lazily, once per page, and only when this renders
 * (sign-in pages only). No client id, or a script that fails to load → this
 * renders nothing at all and says so through `onUnavailable`, so the form
 * never shows a broken button or a lonely divider.
 *
 * Plain markup + tokens only — no design-system import, so the button cannot
 * pull the DS into anything that reuses it.
 */
import { useEffect, useRef, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

const GIS_SRC = "https://accounts.google.com/gsi/client";
/** GIS caps the rendered button width at 400px. */
const GIS_MAX_WIDTH = 400;

interface GisId {
  initialize: (config: {
    client_id: string;
    callback: (response: { credential?: string }) => void;
  }) => void;
  renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
}
type GisWindow = Window & { google?: { accounts?: { id?: GisId } } };

let gisLoad: Promise<GisId> | null = null;

function loadGis(): Promise<GisId> {
  gisLoad ??= new Promise<GisId>((resolve, reject) => {
    const ready = (): void => {
      const id = (window as GisWindow).google?.accounts?.id;
      if (id !== undefined) resolve(id);
      else reject(new Error("GIS unavailable"));
    };
    const script = document.createElement("script");
    script.src = GIS_SRC;
    script.async = true;
    script.onload = ready;
    script.onerror = () => reject(new Error("GIS failed to load"));
    document.head.appendChild(script);
  }).catch((err: unknown) => {
    gisLoad = null; // let a later mount try again
    throw err;
  });
  return gisLoad;
}

export interface GoogleSignInButtonProps {
  /** OAuth web client id. Empty/absent → renders nothing. */
  clientId: string | undefined;
  /** Receives Google's ID token (`response.credential`). */
  onCredential: (idToken: string) => void;
  /** The script failed to load — nothing will render. */
  onUnavailable?: () => void;
}

export function GoogleSignInButton({
  clientId,
  onCredential,
  onUnavailable,
}: GoogleSignInButtonProps): JSX.Element | null {
  const { i18n } = useTranslation("auth");
  const slot = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");
  // The latest handler, without re-rendering Google's button on every render.
  const handler = useRef(onCredential);
  handler.current = onCredential;
  const unavailable = useRef(onUnavailable);
  unavailable.current = onUnavailable;
  const locale = i18n.language;

  useEffect(() => {
    if (clientId === undefined || clientId === "") return;
    let live = true;
    loadGis()
      .then((gis) => {
        if (!live || slot.current === null) return;
        gis.initialize({
          client_id: clientId,
          callback: (response) => {
            if (typeof response.credential === "string" && response.credential !== "") {
              handler.current(response.credential);
            }
          },
        });
        slot.current.replaceChildren();
        gis.renderButton(slot.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          shape: "rectangular",
          text: "continue_with",
          logo_alignment: "center",
          locale,
          width: Math.min(slot.current.offsetWidth || GIS_MAX_WIDTH, GIS_MAX_WIDTH),
        });
        setState("ready");
      })
      .catch(() => {
        if (!live) return;
        setState("failed");
        unavailable.current?.();
      });
    return () => {
      live = false;
    };
  }, [clientId, locale]);

  if (clientId === undefined || clientId === "" || state === "failed") return null;

  return (
    // `invisible`, not `hidden`, while loading: the slot must keep its width so
    // Google's button is sized to the column (phone width included), and its
    // height (GIS "large" = 40px) so nothing below jumps when it arrives.
    <div
      ref={slot}
      className={`flex min-h-10 w-full justify-center ${state === "ready" ? "" : "invisible"}`}
    />
  );
}
